import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { usePager } from "../components/Pagination";
import PageHeader from "../components/PageHeader";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Plus, LogOut as LogOutIcon, UserX, AlertTriangle } from "lucide-react";
import { Dialog, Input } from "./Suppliers";
import { fmtIST } from "../lib/time";
import DeleteRequestButton from "../components/DeleteRequestButton";
import useSubmitGuard from "../lib/useSubmitGuard";

const EMPTY = () => ({
  visitor_name: "", phone: "", company: "", purpose: "",
  host_employee: "", vehicle_number: "",
});

export default function Visitors() {
  const { user } = useAuth();
  // Feb-2026 — guards prevent double-tap duplicates on the security kiosk.
  const { busy: submitBusy, guard: submitGuard, reset: resetGuard } = useSubmitGuard();
  const { busy: blBusy, guard: blGuard, reset: resetBlGuard } = useSubmitGuard();
  const isSA = !!user?.role?.is_super_admin;
  const isPHorSA = isSA || user?.role?.name === "Plant Head/Admin";
  const [rows, setRows] = useState([]);
  const [blacklist, setBlacklist] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY());
  const [showBL, setShowBL] = useState(false);
  const [blForm, setBlForm] = useState({ visitor_name: "", phone: "", reason: "" });

  const load = async () => {
    const r = await api.get("/visitors").catch(() => ({ data: [] }));
    setRows(r.data || []);
    if (isPHorSA) {
      const b = await api.get("/visitors/blacklist").catch(() => ({ data: [] }));
      setBlacklist(b.data || []);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    if (!form.visitor_name.trim() || !form.phone.trim() || !form.purpose.trim() || !form.host_employee.trim()) {
      toast.error("Name, Phone, Purpose, Host are mandatory."); return;
    }
    try {
      await submitGuard(async (idemKey) => {
        const r = await api.post("/visitors", form, { headers: { "Idempotency-Key": idemKey } });
        if (r.data.warning) toast.warning(r.data.warning, { duration: 6000 });
        else toast.success(`Visit recorded · ${r.data.visit_id}`);
      });
      setOpen(false); setForm(EMPTY()); resetGuard(); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const checkout = async (vid) => {
    try { await api.post(`/visitors/${vid}/checkout`); toast.success("Checked out"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const addBlacklist = async () => {
    if (!blForm.visitor_name.trim() || !blForm.phone.trim() || (blForm.reason || "").length < 5) {
      toast.error("Name, phone and reason (≥5 chars) required."); return;
    }
    try {
      await blGuard(async (idemKey) => {
        await api.post("/visitors/blacklist", blForm, { headers: { "Idempotency-Key": idemKey } });
        toast.success("Blacklisted");
      });
      setBlForm({ visitor_name: "", phone: "", reason: "" }); resetBlGuard(); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const removeBlacklist = async (id) => {
    if (!window.confirm("Remove from blacklist?")) return;
    try { await api.delete(`/visitors/blacklist/${id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  // Wave-J pagination
  const { visibleRows: pagedVisitors, controls: visitorControls } = usePager(rows, 10);

  return (
    <div data-testid="visitors-page">
      <PageHeader title="Visitor Management" subtitle="Security check-in / check-out · Host notification · Blacklist"
        helpText="Minimal fields, no photos (Mongo-only). Security records arrival → host notified. Security marks checkout on exit. PH/SA see full log; SA manages blacklist."
        right={
          <div className="flex gap-2">
            {isPHorSA && (
              <button onClick={() => setShowBL(!showBL)} data-testid="toggle-blacklist"
                className="border-2 border-[#C62828] text-[#C62828] hover:bg-[#C62828] hover:text-white text-sm px-4 py-2 rounded-sm flex items-center gap-2 uppercase tracking-wider font-semibold">
                <UserX size={14}/> {showBL ? "Hide" : "Show"} Blacklist ({blacklist.length})
              </button>
            )}
            <button onClick={() => setOpen(true)} className="bg-[#1565C0] hover:bg-[#0D2B5E] text-white text-sm px-4 py-2 rounded-sm flex items-center gap-2 uppercase tracking-wider font-semibold" data-testid="new-visitor-btn">
              <Plus size={14}/> New Visit
            </button>
          </div>
        }/>
      <div className="p-8 space-y-6">
        {showBL && isPHorSA && (
          <section className="bg-white border border-[#C62828]/30 rounded-sm" data-testid="blacklist-section">
            <div className="px-5 py-3 border-b border-[#C62828]/30 flex items-center justify-between bg-[#C62828]/5">
              <div className="text-[10px] tracking-[0.2em] font-bold text-[#C62828] uppercase">Blacklist ({blacklist.length})</div>
            </div>
            <div className="p-4">
              {isSA && (
                <div className="bg-[#F5F5F5] border border-[#EEEEEE] p-3 rounded-sm mb-3 grid grid-cols-4 gap-2 items-end">
                  <Input label="Name" value={blForm.visitor_name} onChange={(v) => setBlForm({ ...blForm, visitor_name: v })} testId="bl-name"/>
                  <Input label="Phone" value={blForm.phone} onChange={(v) => setBlForm({ ...blForm, phone: v })} testId="bl-phone"/>
                  <Input label="Reason (≥5 chars)" value={blForm.reason} onChange={(v) => setBlForm({ ...blForm, reason: v })} testId="bl-reason"/>
                  <button onClick={addBlacklist} disabled={blBusy} data-testid="bl-add"
                    className="bg-[#C62828] text-white text-[10px] tracking-widest uppercase font-bold px-3 py-2 rounded-sm disabled:opacity-50 disabled:cursor-not-allowed">{blBusy ? "Adding…" : "Add to Blacklist"}</button>
                </div>
              )}
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-widest text-[#0D2B5E]/60">
                  <tr><th className="text-left px-3 py-1">Name</th><th className="text-left px-3 py-1">Phone</th><th className="text-left px-3 py-1">Reason</th><th className="text-left px-3 py-1">Added</th><th className="text-right px-3 py-1">Action</th></tr>
                </thead>
                <tbody>
                  {blacklist.map((b) => (
                    <tr key={b.id} className="border-t border-[#EEEEEE]">
                      <td className="px-3 py-1.5 font-mono">{b.visitor_name}</td>
                      <td className="px-3 py-1.5 font-mono">{b.phone}</td>
                      <td className="px-3 py-1.5 text-xs">{b.reason}</td>
                      <td className="px-3 py-1.5 text-[10px] text-[#0D2B5E]/60 font-mono">{fmtIST(b.added_at)?.slice(0, 10)} · {b.added_by}</td>
                      <td className="px-3 py-1.5 text-right">
                        {isSA && <button onClick={() => removeBlacklist(b.id)} data-testid={`bl-remove-${b.id}`} className="text-[10px] tracking-widest uppercase font-bold text-[#C62828] border border-[#C62828] hover:bg-[#C62828] hover:text-white px-2 py-0.5 rounded-sm">Remove</button>}
                      </td>
                    </tr>
                  ))}
                  {blacklist.length === 0 && <tr><td colSpan={5} className="p-3 text-center text-sm text-[#0D2B5E]/50">No entries.</td></tr>}
                </tbody>
              </table>
            </div>
          </section>
        )}
        <section className="bg-white border border-[#EEEEEE] rounded-sm">
          <div className="px-5 py-3 border-b border-[#EEEEEE] bg-[#F5F5F5]">
            <div className="text-[10px] tracking-[0.2em] font-bold text-[#0D2B5E] uppercase">Visitor Log ({rows.length})</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F5F5F5] text-[10px] uppercase tracking-widest text-[#0D2B5E]/60">
                <tr>
                  <th className="text-left px-4 py-2">Visit #</th>
                  <th className="text-left px-4 py-2">Visitor</th>
                  <th className="text-left px-4 py-2">Phone</th>
                  <th className="text-left px-4 py-2">Company</th>
                  <th className="text-left px-4 py-2">Purpose</th>
                  <th className="text-left px-4 py-2">Host</th>
                  <th className="text-left px-4 py-2">Vehicle</th>
                  <th className="text-left px-4 py-2">In (IST)</th>
                  <th className="text-left px-4 py-2">Out (IST)</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-right px-4 py-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {pagedVisitors.map((v) => (
                  <tr key={v.id} className={`border-t border-[#EEEEEE] ${v.is_blacklisted_match ? "bg-[#C62828]/5" : ""}`} data-testid={`visitor-row-${v.id}`}>
                    <td className="px-4 py-2 font-mono text-[#0D2B5E] text-xs">{v.visit_id}</td>
                    <td className="px-4 py-2 font-bold">{v.visitor_name}{v.is_blacklisted_match && <AlertTriangle size={12} className="inline ml-1 text-[#C62828]"/>}</td>
                    <td className="px-4 py-2 font-mono text-xs">{v.phone}</td>
                    <td className="px-4 py-2 text-xs">{v.company || "—"}</td>
                    <td className="px-4 py-2 text-xs">{v.purpose}</td>
                    <td className="px-4 py-2 text-xs">{v.host_employee}</td>
                    <td className="px-4 py-2 text-xs font-mono">{v.vehicle_number || "—"}</td>
                    <td className="px-4 py-2 text-[10px] font-mono text-[#0D2B5E]/60">{fmtIST(v.checked_in_at)}</td>
                    <td className="px-4 py-2 text-[10px] font-mono text-[#0D2B5E]/60">{v.checked_out_at ? fmtIST(v.checked_out_at) : "—"}</td>
                    <td className="px-4 py-2">
                      <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm text-white ${v.status === "in" ? "bg-[#2E7D32]" : "bg-[#0D2B5E]/50"}`}>{v.status}</span>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <div className="inline-flex items-center gap-1">
                        {v.status === "in" && (
                          <button onClick={() => checkout(v.id)} data-testid={`visitor-checkout-${v.id}`} className="text-xs border border-[#1565C0] text-[#1565C0] hover:bg-[#1565C0] hover:text-white px-2 py-1 rounded-sm uppercase tracking-wider font-bold inline-flex items-center gap-1"><LogOutIcon size={12}/>Checkout</button>
                        )}
                        <DeleteRequestButton module="visitor" record={v} label={v.visit_id} onDeleted={load}/>
                      </div>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={11} className="p-6 text-center text-sm text-[#0D2B5E]/50">No visits yet.</td></tr>}
              </tbody>
            </table>
            {visitorControls}
          </div>
        </section>
      </div>

      {open && (
        <Dialog title="New Visitor" onClose={() => { setOpen(false); setForm(EMPTY()); }}>
          <div className="grid grid-cols-2 gap-4">
            <Input label="Visitor Name *" value={form.visitor_name} onChange={(v) => setForm({ ...form, visitor_name: v })} testId="vis-name"/>
            <Input label="Phone *" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="vis-phone"/>
            <Input label="Company" value={form.company} onChange={(v) => setForm({ ...form, company: v })} testId="vis-company"/>
            <Input label="Vehicle No." value={form.vehicle_number} onChange={(v) => setForm({ ...form, vehicle_number: v })} testId="vis-vehicle"/>
            <Input label="Host Employee *" value={form.host_employee} onChange={(v) => setForm({ ...form, host_employee: v })} testId="vis-host"/>
            <Input label="Purpose *" value={form.purpose} onChange={(v) => setForm({ ...form, purpose: v })} testId="vis-purpose"/>
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <button onClick={() => { setOpen(false); setForm(EMPTY()); }} className="px-4 py-2 border border-[#EEEEEE] text-sm rounded-sm">Cancel</button>
            <button onClick={save} disabled={submitBusy} className="px-5 py-2 bg-[#1565C0] hover:bg-[#0D2B5E] text-white text-sm rounded-sm font-bold uppercase tracking-wider disabled:opacity-50 disabled:cursor-not-allowed" data-testid="vis-save">
              {submitBusy ? "Submitting…" : "Record Visit"}
            </button>
          </div>
        </Dialog>
      )}
    </div>
  );
}
